import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calculator, Sparkles, Heart, Eye, TrendingUp, Users, Book, Star,
  Globe, Target, Briefcase, Palette, ChevronDown, ChevronUp, Zap,
  Info, Award, AlertTriangle, Calendar, Edit3
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import CustomTermsPanel from './CustomTermsPanel';
import NumerologyColorLegend from './NumerologyColorLegend';
import NumerologicalTextReflection, {
  getEquilibriumGradientFromText,
  getNumerologyCharacterColor,
  getNumerologyCharacterValue,
  getNumerologyReducedColor
} from './NumerologicalTextReflection';
import { 
  calculateNameNumerology, 
  reduceNumber,
  calculateLifePath,
  calculatePersonalYear,
  calculatePersonalMonth,
  calculatePersonalDay,
  calculateBirthdayNumber,
  getNumberMeaning
} from '../domains/numerology/calculator';
import { 
  getArchetype,
  getArchetypeWithCustomTerms, 
  getConnection, 
  getCompatibleNumbers, 
  getChallengingNumbers,
  getCulturalArchetypes,
  getRelationshipDynamic,
  type NumberArchetype
} from '../domains/numerology/archetypes';
import { 
  PHI, 
  PHI_INVERSE, 
  GOLDEN_ANGLE_DEGREES, 
  PI, 
  E, 
  SQRT_2, 
  SQRT_3, 
  SQRT_5,
  fibonacci,
  fibonacciSequence
} from '../domains/sacred-geometry/mathematics';
import toast from 'react-hot-toast';

/**
 * Full-Featured Numerology Calculator Component
 * 
 * Comprehensive calculator with deep archetypal meanings, connections,
 * and relationships for all numbers 1-9 and master numbers 11, 22, 33, 44.
 */
/**
 * Expandable Archetype Detail Component
 */
interface ArchetypeDetailCardProps {
  archetype: NumberArchetype;
  number: number;
  label: string;
  icon: React.ReactNode;
  colors: any;
  isExpanded: boolean;
  onToggle: () => void;
}

const ArchetypeDetailCard: React.FC<ArchetypeDetailCardProps> = ({
        archetype,
  number,
  label,
  icon,
  colors,
  isExpanded,
  onToggle
}) => {
  const culturalArchetypes = getCulturalArchetypes(number);
  const compatibleNumbers = getCompatibleNumbers(number);
  const challengingNumbers = getChallengingNumbers(number);

  const primaryColor = colors.primary || colors.purple;
  const equilibriumSeed = `${label} ${number} ${archetype.title} ${archetype.essence} ${archetype.lifePurpose}`;
  const equilibriumGradient = getEquilibriumGradientFromText(equilibriumSeed, primaryColor);
  const equilibriumAccent = getNumerologyReducedColor(number);

  const renderNumerologicalPhrase = (phrase: string, neutralColor: string = colors.gray) => {
    return phrase.split('').map((char, idx) => {
      const value = getNumerologyCharacterValue(char);
      return (
        <span key={`${char}-${idx}`} style={{ color: value === null ? neutralColor : getNumerologyCharacterColor(char) }}>
          {char}
        </span>
      );
    });
  };

  return (
    <div style={{
      background: `
        linear-gradient(135deg,
          rgba(8, 12, 20, 0.9) 0%,
          rgba(15, 23, 42, 0.86) 45%,
          rgba(30, 41, 59, 0.84) 100%
        ),
        ${equilibriumGradient}
      `,
      border: `2px solid ${equilibriumAccent}`,
              borderRadius: '4px',
              boxShadow: `
                inset 0 1px 3px rgba(255,255,255,0.1),
                inset 0 -1px 3px rgba(0,0,0,0.5),
                0 2px 4px rgba(0,0,0,0.6),
                0 0 0 1px #404040,
        0 0 8px ${equilibriumAccent}55
      `,
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div 
        onClick={onToggle}
          style={{
          padding: '1rem',
          cursor: 'pointer',
            display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          transition: 'background 0.3s ease',
          position: 'relative'
          }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = getEquilibriumGradientFromText(equilibriumSeed, primaryColor);
          const btn = e.currentTarget.querySelector('button');
          if (btn) {
            btn.style.background = `${equilibriumAccent}20`;
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          const btn = e.currentTarget.querySelector('button');
          if (btn) {
            btn.style.background = 'transparent';
          }
        }}
        >
        {icon}
        <div style={{ flex: 1 }}>
          <div style={{
            color: primaryColor, 
                fontSize: '0.9rem', 
                fontFamily: 'monospace', 
                fontWeight: '700',
            marginBottom: '0.25rem'
              }}>
            {renderNumerologicalPhrase(`${label.toUpperCase()}: ${number}${archetype.isMaster ? ' (MASTER)' : ''}`, colors.light)}
            </div>
              <div style={{ 
            color: primaryColor, 
                fontSize: '1.25rem', 
                fontWeight: '600',
                marginBottom: '0.25rem'
              }}>
            {renderNumerologicalPhrase(archetype.title, colors.light)}
              </div>
              <div style={{ 
                color: colors.gray, 
                fontSize: '0.75rem',
            fontFamily: 'monospace'
              }}>
            {renderNumerologicalPhrase(archetype.essence, colors.gray)}
              </div>
              {!isExpanded && (
                <div style={{
                  marginTop: '0.5rem',
                  fontSize: '0.65rem',
                  color: primaryColor,
                  fontStyle: 'italic',
                  opacity: 0.7
                }}>
                  Click to expand for full details
                </div>
              )}
        </div>
        <button 
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          style={{
            background: 'transparent',
            border: 'none',
            color: primaryColor,
            cursor: 'pointer',
            padding: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            borderRadius: '4px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = `${equilibriumAccent}20`;
            e.currentTarget.style.color = primaryColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = primaryColor;
          }}
          title={isExpanded ? 'Collapse details' : 'Expand for full archetype details'}
        >
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
            </div>

      {/* Expanded Details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{ padding: '1rem', borderTop: `1px solid ${primaryColor}20` }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* Core Meanings */}
                <div>
                  <div style={{ 
                    color: primaryColor, 
                    fontSize: '0.75rem', 
                    fontFamily: 'monospace',
                    fontWeight: '600',
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Book size={14} /> CORE MEANINGS
                  </div>
              <div style={{ 
                color: colors.light, 
                fontSize: '0.8rem', 
                lineHeight: '1.6',
                marginBottom: '0.5rem'
              }}>
                  <strong>Core:</strong> {renderNumerologicalPhrase(archetype.coreMeaning, colors.light)}
                  </div>
                  <div style={{ 
                    color: colors.light, 
                    fontSize: '0.8rem', 
                    lineHeight: '1.6',
                    marginBottom: '0.5rem'
                  }}>
                      <strong>Symbolic:</strong> {renderNumerologicalPhrase(archetype.symbolicMeaning, colors.light)}
              </div>
              <div style={{ 
                color: colors.gray, 
                fontSize: '0.75rem', 
                lineHeight: '1.5',
                fontStyle: 'italic'
              }}>
                  <strong>Spiritual:</strong> {renderNumerologicalPhrase(archetype.spiritualMeaning, colors.gray)}
              </div>
            </div>

                {/* Life Purpose & Lessons */}
                <div>
              <div style={{ 
                    color: primaryColor, 
                fontSize: '0.75rem', 
                fontFamily: 'monospace',
                fontWeight: '600',
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
              }}>
                    <Target size={14} /> LIFE PURPOSE & LESSONS
              </div>
              <div style={{ 
                    color: colors.light, 
                    fontSize: '0.8rem', 
                    lineHeight: '1.6',
                    marginBottom: '0.75rem'
                  }}>
                    {renderNumerologicalPhrase(archetype.lifePurpose, colors.light)}
                  </div>
                  {archetype.lifeLessons.length > 0 && (
                    <div>
                      <div style={{ 
                        color: colors.gray, 
                        fontSize: '0.7rem',
                        marginBottom: '0.5rem'
                      }}>
                        Life Lessons:
                      </div>
                      <ul style={{ 
                        listStyle: 'none', 
                        padding: 0, 
                        margin: 0,
                display: 'flex', 
                        flexDirection: 'column',
                        gap: '0.25rem'
                      }}>
                        {archetype.lifeLessons.map((lesson, idx) => (
                          <li key={idx} style={{
                            color: colors.light,
                            fontSize: '0.75rem',
                            paddingLeft: '1rem',
                            position: 'relative'
                          }}>
                            <span style={{ 
                              position: 'absolute', 
                              left: 0, 
                              color: primaryColor 
                            }}>•</span>
                            {renderNumerologicalPhrase(lesson, colors.light)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Strengths & Challenges Grid */}
                <div>
                  <div style={{ 
                    color: primaryColor, 
                    fontSize: '0.75rem', 
                    fontFamily: 'monospace',
                    fontWeight: '600',
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Award size={14} /> STRENGTHS & CHALLENGES
                  </div>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '1rem' 
                  }}>
                    <div>
                      <div style={{ 
                        color: colors.emerald, 
                fontSize: '0.7rem',
                        fontWeight: '600',
                        marginBottom: '0.5rem',
                        paddingBottom: '0.5rem',
                        borderBottom: `1px solid ${colors.emerald}30`
              }}>
                        Strengths
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {archetype.strengths.map((s, i) => (
                  <span 
                    key={i}
                    style={{
                      padding: '0.25rem 0.5rem',
                      background: getEquilibriumGradientFromText(`${s} ${archetype.title}`, colors.emerald),
                      border: `1px solid ${getNumerologyReducedColor(number + i + 1)}`,
                      borderRadius: '4px',
                              fontSize: '0.7rem',
                              color: colors.emerald,
                      fontFamily: 'monospace'
                    }}
                  >
                    {renderNumerologicalPhrase(s, colors.emerald)}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div style={{ 
                color: colors.amber, 
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        marginBottom: '0.5rem',
                        paddingBottom: '0.5rem',
                        borderBottom: `1px solid ${colors.amber}30`
                      }}>
                        Challenges
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {archetype.challenges.map((c, i) => (
                          <span 
                            key={i}
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: getEquilibriumGradientFromText(`${c} ${archetype.title}`, colors.amber),
                              border: `1px solid ${getNumerologyReducedColor(number + i + 2)}`,
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                              color: colors.amber,
                              fontFamily: 'monospace'
                            }}
                          >
                            {renderNumerologicalPhrase(c, colors.amber)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Talents & Shadow */}
                <div>
                  <div style={{ 
                    color: primaryColor, 
                fontSize: '0.75rem', 
                fontFamily: 'monospace',
                fontWeight: '600',
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
              }}>
                    <Sparkles size={14} /> TALENTS & SHADOW ASPECTS
              </div>
              <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '1rem' 
                  }}>
                    <div>
                      <div style={{ 
                        color: colors.emerald, 
                fontSize: '0.7rem',
                        fontWeight: '600',
                        marginBottom: '0.5rem'
              }}>
                        Talents
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {archetype.talents.map((t, i) => (
                  <span 
                    key={i}
                    style={{
                      padding: '0.25rem 0.5rem',
                              background: getEquilibriumGradientFromText(`${t} ${archetype.title}`, colors.emerald),
                              border: `1px solid ${getNumerologyReducedColor(number + i + 3)}`,
                      borderRadius: '4px',
                              fontSize: '0.7rem',
                              color: colors.emerald
                            }}
                          >
                            {renderNumerologicalPhrase(t, colors.emerald)}
                  </span>
                ))}
                      </div>
                    </div>
                    <div>
                      <div style={{ 
                        color: colors.rose, 
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        marginBottom: '0.5rem'
                      }}>
                        Shadow Aspects
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {archetype.shadowAspects.map((s, i) => (
                          <span 
                            key={i}
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: getEquilibriumGradientFromText(`${s} ${archetype.title}`, colors.rose),
                              border: `1px solid ${getNumerologyReducedColor(number + i + 4)}`,
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                              color: colors.rose
                            }}
                          >
                            {renderNumerologicalPhrase(s, colors.rose)}
                          </span>
                        ))}
                      </div>
              </div>
            </div>
          </div>

                {/* Sacred Connections */}
                <div>
                  <div style={{ 
                    color: primaryColor, 
                    fontSize: '0.75rem', 
                    fontFamily: 'monospace',
                    fontWeight: '600',
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Star size={14} /> SACRED CONNECTIONS
                  </div>
          <div style={{
            display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
            gap: '0.75rem'
          }}>
                    <div>
                      <div style={{ color: colors.gray, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Color</div>
                      <div style={{ color: colors.light, fontSize: '0.75rem' }}>{renderNumerologicalPhrase(archetype.color, colors.light)}</div>
                    </div>
                    <div>
                      <div style={{ color: colors.gray, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Element</div>
                      <div style={{ color: colors.light, fontSize: '0.75rem' }}>{renderNumerologicalPhrase(archetype.element, colors.light)}</div>
                    </div>
                    {archetype.planet && (
                      <div>
                        <div style={{ color: colors.gray, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Planet</div>
                        <div style={{ color: colors.light, fontSize: '0.75rem' }}>{renderNumerologicalPhrase(archetype.planet, colors.light)}</div>
                      </div>
                    )}
                    {archetype.chakra && (
                      <div>
                        <div style={{ color: colors.gray, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Chakra</div>
                        <div style={{ color: colors.light, fontSize: '0.75rem' }}>{renderNumerologicalPhrase(archetype.chakra, colors.light)}</div>
                      </div>
                    )}
                    <div>
                      <div style={{ color: colors.gray, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Frequency</div>
                      <div style={{ color: colors.light, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                        {archetype.frequency} Hz
                      </div>
                    </div>
                  </div>
                </div>

                {/* Symbolic Associations */}
                {archetype.symbols.length > 0 && (
                  <div>
            <div style={{
                      color: primaryColor, 
                      fontSize: '0.75rem', 
                      fontFamily: 'monospace',
                      fontWeight: '600',
                      marginBottom: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <Palette size={14} /> SYMBOLIC ASSOCIATIONS
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {archetype.symbols.map((symbol, idx) => (
                        <span 
                          key={idx}
                          style={{
                            padding: '0.4rem 0.75rem',
                            background: getEquilibriumGradientFromText(symbol, primaryColor),
                            border: `1px solid ${getNumerologyReducedColor(number + idx + 7)}66`,
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            color: primaryColor
                          }}
                        >
                          {renderNumerologicalPhrase(symbol, primaryColor)}
                        </span>
                      ))}
                    </div>
                    {archetype.mythologicalFigures && archetype.mythologicalFigures.length > 0 && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <div style={{ color: colors.gray, fontSize: '0.7rem', marginBottom: '0.5rem' }}>
                          Mythological Figures:
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {archetype.mythologicalFigures.map((figure, idx) => (
                            <span 
                              key={idx}
                              style={{
                                padding: '0.4rem 0.75rem',
                                background: getEquilibriumGradientFromText(figure, primaryColor),
                                border: `1px solid ${getNumerologyReducedColor(number + idx + 8)}66`,
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                color: primaryColor
                              }}
                            >
                              {renderNumerologicalPhrase(figure, primaryColor)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Career Paths */}
                {archetype.careerPaths.length > 0 && (
                  <div>
                <div style={{ 
                      color: primaryColor, 
                  fontSize: '0.75rem', 
                  fontFamily: 'monospace',
                      fontWeight: '600',
                      marginBottom: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <Briefcase size={14} /> CAREER PATHS
                </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      {archetype.careerPaths.map((path, idx) => (
                        <span 
                          key={idx}
                          style={{
                            padding: '0.4rem 0.75rem',
                            background: getEquilibriumGradientFromText(path, primaryColor),
                            border: `1px solid ${getNumerologyReducedColor(number + idx + 9)}66`,
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            color: primaryColor
                          }}
                        >
                          {renderNumerologicalPhrase(path, primaryColor)}
                        </span>
                      ))}
              </div>
              <div style={{ 
                color: colors.light, 
                      fontSize: '0.75rem', 
                      lineHeight: '1.5',
                      fontStyle: 'italic'
                    }}>
                      <strong>Vocational Calling:</strong> {renderNumerologicalPhrase(archetype.vocationalCalling, colors.light)}
                    </div>
                  </div>
                )}

                {/* Relationship Dynamics */}
                <div>
                  <div style={{ 
                    color: primaryColor, 
                    fontSize: '0.75rem', 
                    fontFamily: 'monospace',
                    fontWeight: '600',
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Users size={14} /> RELATIONSHIP DYNAMICS
                  </div>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '1rem' 
                  }}>
                    <div>
                      <div style={{ 
                        color: colors.emerald, 
                fontSize: '0.7rem',
                        fontWeight: '600',
                        marginBottom: '0.5rem'
                      }}>
                        Compatible Numbers
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {compatibleNumbers.map((num) => {
                          const compatArch = getArchetype(num);
                          return (
                            <span 
                              key={num}
                              style={{
                                padding: '0.4rem 0.75rem',
                                background: getEquilibriumGradientFromText(`${num} ${compatArch.title}`, colors.emerald),
                                border: `1px solid ${compatArch.color || colors.emerald}`,
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                color: colors.emerald,
                                fontFamily: 'monospace',
                fontWeight: '600'
                              }}
                            >
                              {num}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <div style={{ 
                        color: colors.amber, 
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        marginBottom: '0.5rem'
                      }}>
                        Challenging Numbers
              </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {challengingNumbers.map((num) => {
                          const challengeArch = getArchetype(num);
                          return (
                            <span 
                              key={num}
                              style={{
                                padding: '0.4rem 0.75rem',
                                background: getEquilibriumGradientFromText(`${num} ${challengeArch.title}`, colors.amber),
                                border: `1px solid ${challengeArch.color || colors.amber}`,
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                color: colors.amber,
                                fontFamily: 'monospace',
                                fontWeight: '600'
                              }}
                            >
                              {num}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cultural Archetype Mappings */}
                {culturalArchetypes.length > 0 && (
                  <div>
              <div style={{ 
                      color: primaryColor, 
                      fontSize: '0.75rem', 
                      fontFamily: 'monospace',
                      fontWeight: '600',
                      marginBottom: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <Globe size={14} /> CULTURAL ARCHETYPE MAPPINGS
                    </div>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                      gap: '0.75rem' 
                    }}>
                      {culturalArchetypes.map((cultural, idx) => (
                        <div 
                          key={idx}
                          style={{
                            padding: '0.75rem',
                            background: getEquilibriumGradientFromText(`${cultural.tradition} ${cultural.meaning}`, primaryColor),
                            border: `1px solid ${getNumerologyReducedColor(number + idx + 5)}55`,
                            borderRadius: '4px'
                          }}
                        >
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '0.5rem'
                          }}>
                            <strong style={{ color: primaryColor, fontSize: '0.75rem' }}>
                              {cultural.tradition}
                            </strong>
                            {cultural.luckyStatus && (
                              <span style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.65rem',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                ...(cultural.luckyStatus === 'lucky' ? {
                                  background: `rgba(16, 185, 129, 0.2)`,
                                  color: colors.emerald,
                                  border: `1px solid ${colors.emerald}30`
                                } : cultural.luckyStatus === 'unlucky' ? {
                                  background: `rgba(239, 68, 68, 0.2)`,
                                  color: colors.rose,
                                  border: `1px solid ${colors.rose}30`
                                } : cultural.luckyStatus === 'sacred' ? {
                                  background: `rgba(139, 92, 246, 0.2)`,
                                  color: colors.purple,
                                  border: `1px solid ${colors.purple}30`
                                } : {
                                  background: `rgba(255, 255, 255, 0.1)`,
                color: colors.gray, 
                                  border: `1px solid rgba(255, 255, 255, 0.2)`
                                })
                              }}>
                                {cultural.luckyStatus}
                              </span>
                            )}
                          </div>
                          <p style={{ 
                            color: colors.light, 
                            fontSize: '0.7rem', 
                            lineHeight: '1.5',
                            margin: '0 0 0.5rem 0'
                          }}>
                            {renderNumerologicalPhrase(cultural.meaning, colors.light)}
                          </p>
                          {cultural.symbolism.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginBottom: '0.5rem' }}>
                              {cultural.symbolism.map((symbol, sIdx) => (
                                <span 
                                  key={sIdx}
                                  style={{
                                    padding: '0.2rem 0.4rem',
                                    background: getEquilibriumGradientFromText(symbol, primaryColor),
                                    border: `1px solid ${getNumerologyReducedColor(number + sIdx + 6)}55`,
                                    borderRadius: '3px',
                fontSize: '0.65rem',
                                    color: primaryColor
                                  }}
                                >
                                  {renderNumerologicalPhrase(symbol, primaryColor)}
                                </span>
                              ))}
                            </div>
                          )}
                          {cultural.notes && (
                            <p style={{ 
                              color: colors.gray, 
                              fontSize: '0.65rem', 
                              fontStyle: 'italic',
                              margin: 0
                            }}>
                              {renderNumerologicalPhrase(cultural.notes, colors.gray)}
                            </p>
                          )}
              </div>
                      ))}
            </div>
                  </div>
                )}

                <NumerologicalTextReflection
                  text={`${label} • ${archetype.title} • ${archetype.essence} • ${archetype.lifePurpose}`}
                  compact
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const NumerologyCalculator: React.FC = () => {
  const { colors, gradients } = useTheme();
  const [input, setInput] = useState('');
  const [result, setResult] = useState<any>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showCustomTermsPanel, setShowCustomTermsPanel] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    expression: false,
    soulUrge: false,
    personality: false,
    chaldean: false,
    dateCalculations: false,
    sacredGeometry: false
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderNumerologicalPhrase = (phrase: string, neutralColor: string = colors.gray) => {
    return phrase.split('').map((char, idx) => {
      const value = getNumerologyCharacterValue(char);
      return (
        <span key={`${char}-${idx}`} style={{ color: value === null ? neutralColor : getNumerologyCharacterColor(char) }}>
          {char}
        </span>
      );
    });
  };

  // Detect if input contains a date
  const parseDate = (text: string): Date | null => {
    // Try various date formats
    const datePatterns = [
      /(\d{1,2})\/(\d{1,2})\/(\d{4})/,  // MM/DD/YYYY
      /(\d{4})-(\d{1,2})-(\d{1,2})/,   // YYYY-MM-DD
      /(\d{1,2})-(\d{1,2})-(\d{4})/,   // MM-DD-YYYY
    ];
    
    for (const pattern of datePatterns) {
      const match = text.match(pattern);
      if (match) {
        try {
          const date = new Date(text);
          if (!isNaN(date.getTime())) {
            return date;
          }
        } catch (e) {
          // Continue to next pattern
        }
      }
    }
    return null;
  };

  const handleCalculate = async () => {
    if (!input.trim()) {
      toast.error('Please enter text to calculate');
      return;
    }

    setIsCalculating(true);
    try {
      // Calculate Pythagorean system (default)
      const pythagoreanResult = calculateNameNumerology(input, 'pythagorean');
      
      // Calculate Chaldean system
      const chaldeanResult = calculateNameNumerology(input, 'chaldean');
      
      // Try to parse date from input
      const parsedDate = parseDate(input);
      let dateCalculations = null;
      
      if (parsedDate) {
        try {
          const lifePath = calculateLifePath(parsedDate);
          const personalYear = calculatePersonalYear(parsedDate);
          const personalMonth = calculatePersonalMonth(parsedDate);
          const personalDay = calculatePersonalDay(parsedDate);
          const birthday = calculateBirthdayNumber(parsedDate);
          
          dateCalculations = {
            lifePath,
            personalYear,
            personalMonth,
            personalDay,
            birthday,
            birthdayArchetype: getArchetypeWithCustomTerms(birthday)
          };
        } catch (e) {
          console.warn('Date calculation error:', e);
        }
      }
      
      // Calculate sacred geometry numbers
      const cleanText = input.toUpperCase().replace(/[^A-Z]/g, '');
      const textLength = cleanText.length;
      const textSum = pythagoreanResult.expressionNumber;
      
      // Fibonacci sequence up to text length
      const fibSequence = fibonacciSequence(Math.min(textLength + 5, 20));
      const closestFib = fibSequence.reduce((prev, curr) => 
        Math.abs(curr - textLength) < Math.abs(prev - textLength) ? curr : prev
      );
      
      // Golden ratio calculations
      const goldenRatioValue = PHI;
      const goldenAngleValue = GOLDEN_ANGLE_DEGREES;
      const goldenRatioReduced = reduceNumber(Math.round(PHI * 100));
      
      // Sacred constants reduced
      const piReduced = reduceNumber(Math.round(PI * 100));
      const eReduced = reduceNumber(Math.round(E * 100));
      const sqrt2Reduced = reduceNumber(Math.round(SQRT_2 * 100));
      const sqrt3Reduced = reduceNumber(Math.round(SQRT_3 * 100));
      const sqrt5Reduced = reduceNumber(Math.round(SQRT_5 * 100));
      
      // Build comprehensive result
      const fullResult = {
        text: input,
        
        // Pythagorean System (Primary)
        pythagorean: {
          calculation: pythagoreanResult,
          expressionNumber: pythagoreanResult.expressionNumber,
          soulUrgeNumber: pythagoreanResult.soulUrgeNumber,
          personalityNumber: pythagoreanResult.personalityNumber,
          expressionArchetype: getArchetypeWithCustomTerms(pythagoreanResult.expressionNumber),
          soulUrgeArchetype: getArchetypeWithCustomTerms(pythagoreanResult.soulUrgeNumber),
          personalityArchetype: getArchetypeWithCustomTerms(pythagoreanResult.personalityNumber),
          compatibleNumbers: getCompatibleNumbers(pythagoreanResult.expressionNumber),
          challengingNumbers: getChallengingNumbers(pythagoreanResult.expressionNumber),
          letterBreakdown: pythagoreanResult.letterBreakdown
        },
        
        // Chaldean System
        chaldean: {
          calculation: chaldeanResult,
          expressionNumber: chaldeanResult.expressionNumber,
          soulUrgeNumber: chaldeanResult.soulUrgeNumber,
          personalityNumber: chaldeanResult.personalityNumber,
          expressionArchetype: getArchetypeWithCustomTerms(chaldeanResult.expressionNumber),
          soulUrgeArchetype: getArchetypeWithCustomTerms(chaldeanResult.soulUrgeNumber),
          personalityArchetype: getArchetypeWithCustomTerms(chaldeanResult.personalityNumber),
          letterBreakdown: chaldeanResult.letterBreakdown
        },
        
        // Date-based Calculations (if date found)
        dateCalculations,
        
        // Sacred Geometry Numbers
        sacredGeometry: {
          textLength,
          closestFibonacci: closestFib,
          fibonacciSequence: fibSequence.slice(0, 10),
          goldenRatio: {
            value: goldenRatioValue,
            reduced: goldenRatioReduced,
            archetype: getArchetypeWithCustomTerms(goldenRatioReduced),
            meaning: 'Divine proportion, natural beauty, perfect harmony'
          },
          goldenAngle: {
            degrees: goldenAngleValue,
            reduced: reduceNumber(Math.round(goldenAngleValue)),
            archetype: getArchetypeWithCustomTerms(reduceNumber(Math.round(goldenAngleValue))),
            meaning: 'Natural growth pattern angle in nature'
          },
          sacredConstants: {
            pi: { value: PI, reduced: piReduced, archetype: getArchetypeWithCustomTerms(piReduced) },
            e: { value: E, reduced: eReduced, archetype: getArchetypeWithCustomTerms(eReduced) },
            sqrt2: { value: SQRT_2, reduced: sqrt2Reduced, archetype: getArchetypeWithCustomTerms(sqrt2Reduced) },
            sqrt3: { value: SQRT_3, reduced: sqrt3Reduced, archetype: getArchetypeWithCustomTerms(sqrt3Reduced) },
            sqrt5: { value: SQRT_5, reduced: sqrt5Reduced, archetype: getArchetypeWithCustomTerms(sqrt5Reduced) }
          }
        },
        
        // Legacy support (use Pythagorean as default)
        expressionNumber: pythagoreanResult.expressionNumber,
        soulUrgeNumber: pythagoreanResult.soulUrgeNumber,
        personalityNumber: pythagoreanResult.personalityNumber,
        expressionArchetype: getArchetypeWithCustomTerms(pythagoreanResult.expressionNumber),
        soulUrgeArchetype: getArchetypeWithCustomTerms(pythagoreanResult.soulUrgeNumber),
        personalityArchetype: getArchetypeWithCustomTerms(pythagoreanResult.personalityNumber),
        compatibleNumbers: getCompatibleNumbers(pythagoreanResult.expressionNumber),
        challengingNumbers: getChallengingNumbers(pythagoreanResult.expressionNumber),
        calculation: pythagoreanResult,
        letterBreakdown: pythagoreanResult.letterBreakdown
      };
      
      setResult(fullResult);
      toast.success('Full numerology calculated across all systems!');
    } catch (error) {
      console.error('Calculation error:', error);
      toast.error('Failed to calculate numerology');
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Input Section */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <Calculator size={20} color={colors.purple} />
            <h3 style={{ color: colors.purple, fontSize: '1rem', fontFamily: 'monospace', margin: 0 }}>
              NUMEROLOGY CALCULATOR
            </h3>
          </div>
          <motion.button
            onClick={() => setShowCustomTermsPanel(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: '0.5rem 1rem',
              background: `rgba(139, 92, 246, 0.2)`,
              border: `1px solid ${colors.purple}50`,
              borderRadius: '8px',
              color: colors.purple,
              fontSize: '0.75rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontFamily: 'monospace',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `rgba(139, 92, 246, 0.3)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = `rgba(139, 92, 246, 0.2)`;
            }}
          >
            <Edit3 size={14} />
            Custom Terms
          </motion.button>
        </div>
        
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <input
            type="text"
            name="numerology-input"
            aria-label="Text to calculate numerology for"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCalculate()}
            placeholder="Enter text, name, date, concept, or event..."
            style={{
              flex: 1,
              padding: '0.75rem',
              background: `
                linear-gradient(135deg, 
                  #2d2d2d 0%, 
                  #1e1e1e 25%, 
                  #2a2a2a 50%, 
                  #1e1e1e 75%, 
                  #2d2d2d 100%
                )
              `,
              border: '2px solid #0a0a0a',
              borderRadius: '4px',
              boxShadow: `
                inset 0 1px 3px rgba(255,255,255,0.1),
                inset 0 -1px 3px rgba(0,0,0,0.5),
                0 2px 4px rgba(0,0,0,0.6),
                0 0 0 1px #404040
              `,
                color: colors.light, 
              fontSize: '0.875rem',
              fontFamily: 'monospace',
              outline: 'none'
            }}
          />
          <motion.button
            onClick={handleCalculate}
            disabled={isCalculating}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: '0.75rem 1.5rem',
            background: `
              linear-gradient(135deg, 
                #2d2d2d 0%, 
                #1e1e1e 25%, 
                #2a2a2a 50%, 
                #1e1e1e 75%, 
                #2d2d2d 100%
              )
            `,
            border: `2px solid ${colors.purple}`,
            borderRadius: '4px',
            boxShadow: `
              inset 0 1px 3px rgba(255,255,255,0.1),
              inset 0 -1px 3px rgba(0,0,0,0.5),
              0 2px 4px rgba(0,0,0,0.6),
              0 0 0 1px #404040,
                0 0 8px ${colors.purple}40
            `,
              color: colors.purple, 
              fontWeight: '600',
              cursor: isCalculating ? 'wait' : 'pointer',
              fontSize: '0.875rem',
              fontFamily: 'monospace',
              opacity: isCalculating ? 0.6 : 1,
              outline: 'none'
            }}
          >
            {isCalculating ? 'CALC...' : 'CALCULATE'}
          </motion.button>
        </div>

        <div style={{ marginTop: '0.85rem' }}>
          <NumerologyColorLegend compact title="Fr00t Numerology Color Map" />
        </div>
            </div>
            
      {/* Results Section */}
      {result && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem'
          }}
        >
          {/* PYTHAGOREAN SYSTEM - Primary */}
          <div style={{
            background: `
              linear-gradient(135deg, 
                #2d2d2d 0%, 
                #1e1e1e 25%, 
                #2a2a2a 50%, 
                #1e1e1e 75%, 
                #2d2d2d 100%
              )
            `,
            border: `2px solid ${colors.purple}`,
            borderRadius: '4px',
            padding: '1rem',
            marginBottom: '0.5rem'
          }}>
            <div style={{ 
              color: colors.purple, 
              fontSize: '0.75rem', 
              fontFamily: 'monospace',
              fontWeight: '600',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Calculator size={14} /> PYTHAGOREAN SYSTEM (Primary)
            </div>
            
            {/* Expression Number - Full Archetype */}
            <ArchetypeDetailCard
              archetype={result.pythagorean.expressionArchetype}
              number={result.pythagorean.expressionNumber}
              label="Expression"
              icon={<Star size={18} color={colors.purple} />}
              colors={{ ...colors, primary: colors.purple }}
              isExpanded={expandedSections.expression}
              onToggle={() => toggleSection('expression')}
            />

            {/* Soul Urge & Personality Grid */}
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1rem',
              marginTop: '1rem'
            }}>
              {/* Soul Urge */}
              <ArchetypeDetailCard
                archetype={result.pythagorean.soulUrgeArchetype}
                number={result.pythagorean.soulUrgeNumber}
                label="Soul Urge"
                icon={<Heart size={18} color={colors.indigo} />}
                colors={{ ...colors, primary: colors.indigo }}
                isExpanded={expandedSections.soulUrge}
                onToggle={() => toggleSection('soulUrge')}
              />

              {/* Personality */}
              <ArchetypeDetailCard
                archetype={result.pythagorean.personalityArchetype}
                number={result.pythagorean.personalityNumber}
                label="Personality"
                icon={<Eye size={18} color={colors.emerald} />}
                colors={{ ...colors, primary: colors.emerald }}
                isExpanded={expandedSections.personality}
                onToggle={() => toggleSection('personality')}
              />
            </div>
          </div>

          {/* CHALDEAN SYSTEM */}
          <div style={{
            background: `
              linear-gradient(135deg, 
                #2d2d2d 0%, 
                #1e1e1e 25%, 
                #2a2a2a 50%, 
                #1e1e1e 75%, 
                #2d2d2d 100%
              )
            `,
            border: `2px solid ${colors.amber}`,
            borderRadius: '4px',
            padding: '1rem',
            marginBottom: '0.5rem'
          }}>
            <div 
              onClick={() => toggleSection('chaldean')}
              style={{ 
                color: colors.amber, 
                fontSize: '0.75rem', 
                fontFamily: 'monospace',
                fontWeight: '600',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Globe size={14} /> CHALDEAN SYSTEM (Ancient Babylonian)
              </div>
              {expandedSections.chaldean ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
            
            <AnimatePresence>
              {expandedSections.chaldean && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{ overflow: 'hidden' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div style={{ 
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '0.75rem'
                    }}>
                      <div style={{
                        padding: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.2)',
                        border: `1px solid ${colors.amber}30`,
                        borderRadius: '4px'
                      }}>
                        <div style={{ color: colors.gray, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Expression</div>
                        <div style={{ color: colors.amber, fontSize: '1.25rem', fontWeight: '600', fontFamily: 'monospace' }}>
                          {result.chaldean.expressionNumber}
                        </div>
                        <div style={{ color: colors.light, fontSize: '0.7rem', marginTop: '0.25rem' }}>
                          {renderNumerologicalPhrase(result.chaldean.expressionArchetype.title, colors.light)}
                        </div>
                      </div>
                      <div style={{
                        padding: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.2)',
                        border: `1px solid ${colors.amber}30`,
                        borderRadius: '4px'
                      }}>
                        <div style={{ color: colors.gray, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Soul Urge</div>
                        <div style={{ color: colors.amber, fontSize: '1.25rem', fontWeight: '600', fontFamily: 'monospace' }}>
                          {result.chaldean.soulUrgeNumber}
                        </div>
                        <div style={{ color: colors.light, fontSize: '0.7rem', marginTop: '0.25rem' }}>
                          {renderNumerologicalPhrase(result.chaldean.soulUrgeArchetype.title, colors.light)}
                        </div>
                      </div>
                      <div style={{
                        padding: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.2)',
                        border: `1px solid ${colors.amber}30`,
                        borderRadius: '4px'
                      }}>
                        <div style={{ color: colors.gray, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Personality</div>
                        <div style={{ color: colors.amber, fontSize: '1.25rem', fontWeight: '600', fontFamily: 'monospace' }}>
                          {result.chaldean.personalityNumber}
                        </div>
                        <div style={{ color: colors.light, fontSize: '0.7rem', marginTop: '0.25rem' }}>
                          {renderNumerologicalPhrase(result.chaldean.personalityArchetype.title, colors.light)}
                        </div>
                      </div>
                    </div>
                    <div style={{ 
                      color: colors.gray, 
                      fontSize: '0.65rem', 
                      fontStyle: 'italic',
                      padding: '0.5rem',
                      background: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '4px'
                    }}>
                      {renderNumerologicalPhrase('Note: Chaldean system is based on sound vibrations. The number 9 is considered holy and not used in this system.', colors.gray)}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* DATE-BASED CALCULATIONS */}
          {result.dateCalculations && (
            <div style={{
              background: `
                linear-gradient(135deg, 
                  #2d2d2d 0%, 
                  #1e1e1e 25%, 
                  #2a2a2a 50%, 
                  #1e1e1e 75%, 
                  #2d2d2d 100%
                )
              `,
              border: `2px solid ${colors.blue}`,
              borderRadius: '4px',
              padding: '1rem',
              marginBottom: '0.5rem'
            }}>
              <div 
                onClick={() => toggleSection('dateCalculations')}
                style={{ 
                  color: colors.blue, 
                  fontSize: '0.75rem', 
                  fontFamily: 'monospace',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Calendar size={14} /> DATE-BASED CALCULATIONS
                </div>
                {expandedSections.dateCalculations ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </div>
              
              <AnimatePresence>
                {expandedSections.dateCalculations && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ overflow: 'hidden' }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {/* Life Path */}
                      <ArchetypeDetailCard
                        archetype={getArchetypeWithCustomTerms(result.dateCalculations.lifePath.lifePathNumber)}
                        number={result.dateCalculations.lifePath.lifePathNumber}
                        label="Life Path"
                        icon={<Target size={18} color={colors.blue} />}
                        colors={{ ...colors, primary: colors.blue }}
                        isExpanded={false}
                        onToggle={() => {}}
                      />
                      
                      {/* Personal Year/Month/Day Grid */}
                      <div style={{ 
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '0.75rem'
                      }}>
                        <div style={{
                          padding: '0.75rem',
                          background: 'rgba(0, 0, 0, 0.2)',
                          border: `1px solid ${colors.blue}30`,
                          borderRadius: '4px'
                        }}>
                          <div style={{ color: colors.gray, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Personal Year</div>
                          <div style={{ color: colors.blue, fontSize: '1.25rem', fontWeight: '600', fontFamily: 'monospace' }}>
                            {result.dateCalculations.personalYear.personalYearNumber}
                          </div>
                          <div style={{ color: colors.light, fontSize: '0.7rem', marginTop: '0.25rem' }}>
                            {renderNumerologicalPhrase(result.dateCalculations.personalYear.theme, colors.light)}
                          </div>
                        </div>
                        <div style={{
                          padding: '0.75rem',
                          background: 'rgba(0, 0, 0, 0.2)',
                          border: `1px solid ${colors.blue}30`,
                          borderRadius: '4px'
                        }}>
                          <div style={{ color: colors.gray, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Personal Month</div>
                          <div style={{ color: colors.blue, fontSize: '1.25rem', fontWeight: '600', fontFamily: 'monospace' }}>
                            {result.dateCalculations.personalMonth}
                          </div>
                        </div>
                        <div style={{
                          padding: '0.75rem',
                          background: 'rgba(0, 0, 0, 0.2)',
                          border: `1px solid ${colors.blue}30`,
                          borderRadius: '4px'
                        }}>
                          <div style={{ color: colors.gray, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Personal Day</div>
                          <div style={{ color: colors.blue, fontSize: '1.25rem', fontWeight: '600', fontFamily: 'monospace' }}>
                            {result.dateCalculations.personalDay}
                          </div>
                        </div>
                        <div style={{
                          padding: '0.75rem',
                          background: 'rgba(0, 0, 0, 0.2)',
                          border: `1px solid ${colors.blue}30`,
                          borderRadius: '4px'
                        }}>
                          <div style={{ color: colors.gray, fontSize: '0.65rem', marginBottom: '0.25rem' }}>Birthday Number</div>
                          <div style={{ color: colors.blue, fontSize: '1.25rem', fontWeight: '600', fontFamily: 'monospace' }}>
                            {result.dateCalculations.birthday}
                          </div>
                          <div style={{ color: colors.light, fontSize: '0.7rem', marginTop: '0.25rem' }}>
                            {renderNumerologicalPhrase(result.dateCalculations.birthdayArchetype.title, colors.light)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* SACRED GEOMETRY NUMBERS */}
          <div style={{
            background: `
              linear-gradient(135deg, 
                #2d2d2d 0%, 
                #1e1e1e 25%, 
                #2a2a2a 50%, 
                #1e1e1e 75%, 
                #2d2d2d 100%
              )
            `,
            border: `2px solid ${colors.indigo}`,
            borderRadius: '4px',
            padding: '1rem',
            marginBottom: '0.5rem'
          }}>
            <div 
              onClick={() => toggleSection('sacredGeometry')}
              style={{ 
                color: colors.indigo, 
                fontSize: '0.75rem', 
                fontFamily: 'monospace',
                fontWeight: '600',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Sparkles size={14} /> SACRED GEOMETRY & MATHEMATICAL CONSTANTS
              </div>
              {expandedSections.sacredGeometry ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
            
            <AnimatePresence>
              {expandedSections.sacredGeometry && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{ overflow: 'hidden' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {/* Golden Ratio */}
                    <div style={{
                      padding: '0.75rem',
                      background: 'rgba(0, 0, 0, 0.2)',
                      border: `1px solid ${colors.indigo}30`,
                      borderRadius: '4px'
                    }}>
                      <div style={{ color: colors.indigo, fontSize: '0.7rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                        Golden Ratio (φ)
                      </div>
                      <div style={{ color: colors.light, fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                        Value: {result.sacredGeometry.goldenRatio.value.toFixed(6)}
                      </div>
                      <div style={{ color: colors.indigo, fontSize: '1rem', fontWeight: '600', fontFamily: 'monospace', marginBottom: '0.25rem' }}>
                        Reduced: {result.sacredGeometry.goldenRatio.reduced}
                      </div>
                      <div style={{ color: colors.light, fontSize: '0.7rem' }}>
                        {renderNumerologicalPhrase(`${result.sacredGeometry.goldenRatio.archetype.title} - ${result.sacredGeometry.goldenRatio.meaning}`, colors.light)}
                      </div>
                    </div>
                    
                    {/* Fibonacci */}
                    <div style={{
                      padding: '0.75rem',
                      background: 'rgba(0, 0, 0, 0.2)',
                      border: `1px solid ${colors.indigo}30`,
                      borderRadius: '4px'
                    }}>
                      <div style={{ color: colors.indigo, fontSize: '0.7rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                        Fibonacci Sequence
                      </div>
                      <div style={{ color: colors.light, fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                        Text Length: {result.sacredGeometry.textLength} → Closest Fibonacci: {result.sacredGeometry.closestFibonacci}
                      </div>
                      <div style={{ color: colors.gray, fontSize: '0.65rem', fontFamily: 'monospace' }}>
                        Sequence: {result.sacredGeometry.fibonacciSequence.join(', ')}...
                      </div>
                    </div>
                    
                    {/* Sacred Constants Grid */}
                    <div style={{ 
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '0.75rem'
                    }}>
                      {Object.entries(result.sacredGeometry.sacredConstants).map(([key, constant]: [string, any]) => (
                        <div key={key} style={{
                          padding: '0.75rem',
                          background: 'rgba(0, 0, 0, 0.2)',
                          border: `1px solid ${colors.indigo}30`,
                          borderRadius: '4px'
                        }}>
                          <div style={{ color: colors.gray, fontSize: '0.65rem', marginBottom: '0.25rem' }}>
                            {key === 'pi' ? 'π' : key === 'e' ? 'e' : key === 'sqrt2' ? '√2' : key === 'sqrt3' ? '√3' : '√5'}
                          </div>
                          <div style={{ color: colors.indigo, fontSize: '1rem', fontWeight: '600', fontFamily: 'monospace' }}>
                            {constant.reduced}
                          </div>
                          <div style={{ color: colors.light, fontSize: '0.65rem', marginTop: '0.25rem' }}>
                            {renderNumerologicalPhrase(constant.archetype.title, colors.light)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Letter Breakdown - Pythagorean */}
          <div style={{ 
            background: `
              linear-gradient(135deg, 
                #2d2d2d 0%, 
                #1e1e1e 25%, 
                #2a2a2a 50%, 
                #1e1e1e 75%, 
                #2d2d2d 100%
              )
            `,
            border: `2px solid ${colors.purple}`,
            borderRadius: '4px',
            boxShadow: `
              inset 0 1px 3px rgba(255,255,255,0.1),
              inset 0 -1px 3px rgba(0,0,0,0.5),
              0 2px 4px rgba(0,0,0,0.6),
              0 0 0 1px #404040,
              0 0 6px ${colors.purple}20
            `,
            padding: '1rem'
          }}>
            <div style={{ 
              color: colors.purple, 
              fontSize: '0.75rem', 
              fontFamily: 'monospace',
              fontWeight: '600',
              marginBottom: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Info size={14} /> LETTER BREAKDOWN (PYTHAGOREAN)
            </div>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '0.5rem',
              fontFamily: 'monospace',
              fontSize: '0.7rem',
              marginBottom: '0.75rem'
            }}>
              {result.pythagorean.letterBreakdown.map((letter: any, i: number) => (
                <div 
                  key={i}
                  style={{
                    padding: '0.25rem 0.5rem',
                    background: `
                      linear-gradient(135deg, 
                        #2d2d2d 0%, 
                        #1e1e1e 50%, 
                        #2d2d2d 100%
                      )
                    `,
                    border: `2px solid ${letter.type === 'vowel' ? colors.rose : colors.blue}`,
                    borderRadius: '4px',
                    boxShadow: `
                      inset 0 1px 3px rgba(255,255,255,0.1),
                      inset 0 -1px 3px rgba(0,0,0,0.5),
                      0 2px 4px rgba(0,0,0,0.6),
                      0 0 0 1px #404040,
                      0 0 4px ${letter.type === 'vowel' ? colors.rose + '30' : colors.blue + '30'}
                    `,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '2px'
                  }}
                >
                  <div style={{ color: colors.light, fontSize: '0.75rem', fontWeight: '600' }}>
                    {letter.letter}
                  </div>
                  <div style={{ 
                    color: letter.type === 'vowel' ? colors.rose : colors.blue,
                    fontSize: '0.65rem'
                  }}>
                    {letter.value}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ 
              padding: '0.5rem',
              background: `
                linear-gradient(135deg, 
                  #2d2d2d 0%, 
                  #1e1e1e 50%, 
                  #2d2d2d 100%
                )
              `,
              border: `2px solid ${colors.purple}`,
              borderRadius: '4px',
              boxShadow: `
                inset 0 1px 3px rgba(255,255,255,0.1),
                inset 0 -1px 3px rgba(0,0,0,0.5),
                0 2px 4px rgba(0,0,0,0.6),
                0 0 0 1px #404040,
                0 0 4px ${colors.purple}30
              `,
              fontFamily: 'monospace',
              fontSize: '0.65rem',
              color: colors.purple,
              textAlign: 'center'
            }}>
              Total: {result.pythagorean.letterBreakdown.reduce((sum: number, l: any) => sum + l.value, 0)} → {result.pythagorean.expressionNumber}
            </div>
          </div>
        </motion.div>
      )}

      {/* Custom Terms Panel Modal */}
      <AnimatePresence>
        {showCustomTermsPanel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '2rem',
              overflow: 'auto'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowCustomTermsPanel(false);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CustomTermsPanel onClose={() => setShowCustomTermsPanel(false)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NumerologyCalculator;

